function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return; var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import React, { useReducer, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Portal } from 'react-portal';
import { AnimatePresence } from 'framer-motion';
import Container from './Container';
import notificationReducer from './reducer';
import { ADD_NOTIFICATION, REMOVE_NOTIFICATION, UPDATE_NOTIFICATION } from './constants';
import NotificationsList from './NotificationsList';
var NotificationContext = React.createContext();
/**
 * Notification Provider
 *
 * @return {jsx}
 */

export var NotificationProvider = function NotificationProvider(_ref) {
  var autoDismiss = _ref.autoDismiss,
      autoDismissTimeout = _ref.autoDismissTimeout,
      children = _ref.children,
      placement = _ref.placement,
      pauseOnHover = _ref.pauseOnHover;
  var initialState = {
    notificationsCount: 0,
    options: {
      autoDismiss: autoDismiss,
      autoDismissTimeout: autoDismissTimeout,
      pauseOnHover: pauseOnHover
    },
    notifications: []
  };

  var _useReducer = useReducer(notificationReducer, initialState),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      dispatch = _useReducer2[1];

  var notifications = state.notifications;
  /**
   * Add notification
   *
   * @param {function} component - Presentational component for displaying message.
   * @param {object} options - Options passed to heance notification.
   */

  var addNotification = useCallback(function (component) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : state.options;
    var exists = notifications.find(function (n) {
      return n.key === options.key;
    });
    if (!exists) dispatch({
      type: ADD_NOTIFICATION,
      component: component,
      options: options
    });
  }, [notifications, state.options]);
  /**
   * Dismiss notification
   * @param {string} key - unique key to retrieve notification.
   */

  var dismissNotification = useCallback(function (key) {
    var exists = notifications.find(function (n) {
      return n.key === key;
    });
    if (exists) dispatch({
      type: REMOVE_NOTIFICATION,
      key: key
    });
  }, [notifications]);
  /**
   * Update notification component and options
   *
   * @param {function} component - Presentational component for displaying message.
   * @param {object} options - Options passed to heance notification.
   */

  var updateNotification = useCallback(function (component, options) {
    if (!options.key) throw new Error('[updateNotification] key parameter in options is mandatory.');
    var exists = notifications.find(function (n) {
      return n.key === options.key;
    });
    if (exists) dispatch({
      type: UPDATE_NOTIFICATION,
      component: component,
      options: options
    });
  }, [notifications]);
  var getNotification = useCallback(function (key) {
    if (!key) throw new Error('[getNotification] key parameter is mandatory.');
    return notifications.find(function (n) {
      return n.key === key;
    });
  }, [notifications]);
  return /*#__PURE__*/React.createElement(NotificationContext.Provider, {
    value: {
      addNotification: addNotification,
      dismissNotification: dismissNotification,
      getNotification: getNotification,
      updateNotification: updateNotification
    }
  }, children, /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(NotificationsList, {
    placement: placement
  }, /*#__PURE__*/React.createElement(AnimatePresence, null, notifications.map(function (_ref2) {
    var notificationOptions = _ref2.options,
        key = _ref2.key,
        component = _ref2.component;
    return /*#__PURE__*/React.createElement(Container, {
      key: key,
      component: component,
      onDismiss: function onDismiss() {
        return dismissNotification(key);
      },
      autoDismiss: notificationOptions.autoDismiss,
      autoDismissTimeout: notificationOptions.autoDismissTimeout,
      pauseOnHover: notificationOptions.pauseOnHover,
      placement: placement
    });
  })))));
};
/**
 * PropTypes Validation
 */

var bool = PropTypes.bool,
    node = PropTypes.node,
    number = PropTypes.number,
    string = PropTypes.string;
NotificationProvider.propTypes = {
  /**
   * Whether or not to dismiss the notification automatically after a timeout
   */
  autoDismiss: bool,

  /**
   * The time until a notification is automatically dismissed, in milliseconds
   */
  autoDismissTimeout: number,

  /**
   * Anything that can be rendered: numbers, strings, elements or an array (or fragment)
   */
  children: node,

  /**
   * Placement of the notification on the screen
   */
  placement: string,

  /**
   * Whether or not to pause the timeout when hovered
   */
  pauseOnHover: bool
};
/**
 * Default props
 */

NotificationProvider.defaultProps = {
  autoDismiss: false,
  autoDismissTimeout: 3000,
  children: null,
  placement: 'bottom-left',
  pauseOnHover: false
};
export var useNotifications = function useNotifications() {
  var context = useContext(NotificationContext);

  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }

  return context;
};